/**
 * §3 API client.
 *
 * Every call carries `Authorization: Bearer <access_token>` + the
 * current `X-Tenant-ID` header. 401 responses trigger one silent
 * refresh attempt; if that fails the session is cleared and the
 * caller gets the 401 back — `App.tsx` routes an unauthenticated
 * user to /login.
 */

import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  markSessionExpired,
  persistLogin,
  setTenantId,
  type LoginResponse,
} from "./auth";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  auth?: boolean;
}

// §security.single-flight-refresh (2026-06-20) — when several queries 401 at
// once (common on a dashboard: the access token expires while N useQuery calls
// are in flight), they must share ONE /api/admin/refresh round-trip. Without
// this each caller fires its own refresh; with refresh-token ROTATION that
// races — the first rotation invalidates the refresh token the others are still
// holding → spurious logouts. A module-level in-flight promise de-dupes them.
let _refreshInFlight: Promise<boolean> | null = null;

async function _doRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const r = await fetch(`${BASE_URL}/api/admin/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-ID": getTenantId(),
      },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!r.ok) return false;
    const data = (await r.json()) as {
      access_token?: string;
      access_expires_in?: number;
    };
    if (!data.access_token) return false;
    persistLogin({ access_token: data.access_token });
    return true;
  } catch {
    return false;
  }
}

async function attemptRefresh(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = _doRefresh().finally(() => {
    _refreshInFlight = null;
  });
  return _refreshInFlight;
}

export async function api<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const { body, auth = true, headers, ...rest } = init;
  const buildHeaders = (): HeadersInit => {
    const h: Record<string, string> = {
      "X-Tenant-ID": getTenantId(),
      ...(headers as Record<string, string> | undefined),
    };
    if (body !== undefined && !(body instanceof FormData)) {
      h["Content-Type"] ??= "application/json";
    }
    if (auth) {
      const token = getAccessToken();
      if (token) h["Authorization"] = `Bearer ${token}`;
    }
    return h;
  };

  const makeRequest = async (): Promise<Response> => {
    return fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: buildHeaders(),
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  };

  let response = await makeRequest();

  if (response.status === 401 && auth) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      response = await makeRequest();
    } else {
      // §security.session-expired (2026-06-20) — access expired AND refresh
      // failed (refresh token expired/revoked). Flag it (only if we were authed,
      // so it never fires pre-login) so /login shows "your session expired",
      // then clear — clearSession fires the storage event → RequireAuth bounces
      // to /login.
      if (getAccessToken()) markSessionExpired();
      clearSession();
    }
  }

  if (!response.ok) {
    let parsed: unknown = undefined;
    const text = await response.text();
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
    const detail =
      (typeof parsed === "object" && parsed && "detail" in parsed
        ? String((parsed as { detail: unknown }).detail)
        : null) ?? text ?? response.statusText;
    throw new ApiError(detail, response.status, parsed);
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

/**
 * §13.1-hotfix — binary fetch for authenticated media.
 *
 * `<audio src="/api/media/...">` cannot send `Authorization: Bearer`
 * headers; the query-param fallback doesn't work either because
 * the server's JWT check is Bearer-header-only (falling through to
 * the legacy-secret / operator-session check, which rejects JWTs).
 *
 * This helper goes through the normal `fetch` path — same
 * tenant-id header, same Bearer JWT, same 401→refresh retry — and
 * returns the response body as a Blob. The caller wraps it with
 * `URL.createObjectURL(blob)` for `<audio src>` and calls
 * `URL.revokeObjectURL` on unmount to release memory.
 *
 * Trade-off vs. the rejected `?secret=` / signed-URL approaches:
 *   * No HTTP Range → the browser can't seek without the server
 *     cooperating on partial fetches. For the recording sizes we
 *     emit (1–10 MB Opus), the whole Blob loads in well under a
 *     second on any realistic network; seeking still works because
 *     `<audio>` seeks within the already-fetched Blob locally.
 *   * No URL leakage (referer, history, logs) of the token.
 *   * Works across all auth modes (JWT, operator session, legacy)
 *     because it uses the same `makeRequest` path as `api()`.
 */
export async function apiFetchBlob(
  path: string,
  init: Omit<ApiRequestInit, "body"> = {},
): Promise<Blob> {
  const { auth = true, headers, ...rest } = init;
  const buildHeaders = (): HeadersInit => {
    const h: Record<string, string> = {
      "X-Tenant-ID": getTenantId(),
      ...(headers as Record<string, string> | undefined),
    };
    if (auth) {
      const token = getAccessToken();
      if (token) h["Authorization"] = `Bearer ${token}`;
    }
    return h;
  };
  const makeRequest = async (): Promise<Response> =>
    fetch(`${BASE_URL}${path}`, { ...rest, headers: buildHeaders() });
  let response = await makeRequest();
  if (response.status === 401 && auth) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      response = await makeRequest();
    } else {
      // §security.session-expired (2026-06-20) — access expired AND refresh
      // failed (refresh token expired/revoked). Flag it (only if we were authed,
      // so it never fires pre-login) so /login shows "your session expired",
      // then clear — clearSession fires the storage event → RequireAuth bounces
      // to /login.
      if (getAccessToken()) markSessionExpired();
      clearSession();
    }
  }
  if (!response.ok) {
    // Text-parse the error body the same way `api()` does so the
    // caller sees consistent ApiError messages.
    let parsed: unknown = undefined;
    const text = await response.text().catch(() => "");
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
    const detail =
      (typeof parsed === "object" && parsed && "detail" in parsed
        ? String((parsed as { detail: unknown }).detail)
        : null) ?? text ?? response.statusText;
    throw new ApiError(detail, response.status, parsed);
  }
  return response.blob();
}

/**
 * §qa-wave4 (2026-08-10) — authenticated file download.
 *
 * The Orders/Support export buttons and the invoice button used to be
 * bare `<a href download>` / `window.open` hits on authed endpoints —
 * the browser navigation carries no `Authorization: Bearer` header, so
 * every click landed a 401 JSON body instead of a file. This routes the
 * download through `apiFetchBlob` (Bearer + tenant header + the same
 * 401→refresh retry as `api()`) and hands the bytes to the browser via
 * a temporary object URL + programmatic anchor click. The object URL is
 * revoked right after the click to release memory.
 */
export async function apiDownloadFile(
  path: string,
  filename: string,
  init: Omit<ApiRequestInit, "body"> = {},
): Promise<void> {
  const blob = await apiFetchBlob(path, init);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function login(
  username: string,
  password: string,
  bootstrapSecret?: string,
): Promise<LoginResponse> {
  const body: Record<string, string> = { username, password };
  if (bootstrapSecret) body.secret = bootstrapSecret;
  const data = await api<LoginResponse>("/api/admin/login", {
    method: "POST",
    auth: false,
    body,
  });
  persistLogin(data);
  return data;
}

// §v3.pw-reset — self-service password reset (logged-out flow).
// Both are unauthenticated (auth: false). forgotPassword always
// resolves to the same generic message regardless of whether the
// account exists (anti-enumeration is enforced server-side).
// §preauth-identity — `identifier` may be an EMAIL or a username. Usernames
// are auto-derived from the signup email with the `@` stripped, so an owner
// can't guess theirs; email is the identifier they actually hold. `username`
// is still sent so an older backend keeps working.
export async function forgotPassword(
  identifier: string,
): Promise<{ ok: boolean; message: string }> {
  return api<{ ok: boolean; message: string }>(
    "/api/admin/forgot-password",
    { method: "POST", auth: false, body: { identifier, username: identifier } },
  );
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  return api<{ ok: boolean; message: string }>(
    "/api/admin/reset-password",
    { method: "POST", auth: false, body: { token, new_password: newPassword } },
  );
}

// §v3.mfa — login step 2 (unauthenticated; completes a 2FA challenge)
// + authenticator-app enrollment (authenticated; the operator's own).
export async function loginMfa(
  mfaChallenge: string,
  code: string,
): Promise<LoginResponse> {
  const data = await api<LoginResponse>("/api/admin/login/mfa", {
    method: "POST",
    auth: false,
    body: { mfa_challenge: mfaChallenge, code },
  });
  persistLogin(data);
  return data;
}

// §security.mandatory-mfa (2026-06-20) — finish enroll-on-login: trade the
// enrollment challenge + the first TOTP code for tokens (+ one-time backup codes).
export async function loginMfaEnroll(
  mfaChallenge: string,
  code: string,
): Promise<LoginResponse> {
  const data = await api<LoginResponse>("/api/admin/login/mfa-enroll", {
    method: "POST",
    auth: false,
    body: { mfa_challenge: mfaChallenge, code },
  });
  persistLogin(data);
  return data;
}

export async function mfaStatus(): Promise<{ enabled: boolean }> {
  return api<{ enabled: boolean }>("/api/admin/mfa/status", { method: "GET" });
}

export async function mfaSetup(): Promise<{ secret: string; otpauth_uri: string }> {
  return api<{ secret: string; otpauth_uri: string }>("/api/admin/mfa/setup", {
    method: "POST",
    body: {},
  });
}

export async function mfaEnable(
  code: string,
): Promise<{ enabled: boolean; backup_codes: string[] }> {
  return api<{ enabled: boolean; backup_codes: string[] }>(
    "/api/admin/mfa/enable",
    { method: "POST", body: { code } },
  );
}

export async function mfaDisable(code: string): Promise<{ enabled: boolean }> {
  return api<{ enabled: boolean }>("/api/admin/mfa/disable", {
    method: "POST",
    body: { code },
  });
}

// §v3.test-status — automated-test posture for the dashboard reliability panel.
export interface TestStatus {
  suite: { python: number; frontend: number | null; total: number; note: string };
  smoke:
    | {
        ok: boolean;
        passed: number;
        warned: number;
        failed: number;
        total: number;
        ts: number;
        base_url: string;
      }
    | null;
  version: {
    shortSha: string;
    branch: string;
    buildTime: string;
    uptimeSeconds: number;
  };
}

export async function getTestStatus(): Promise<TestStatus> {
  return api<TestStatus>("/api/admin/test-status", { method: "GET" });
}

// §v3.workflow — Release Workflow actions (Run tests / Deploy update).
export async function runSmoke(): Promise<{ ok: boolean; smoke: TestStatus["smoke"]; error?: string }> {
  return api("/api/admin/run-smoke", { method: "POST", body: {} });
}
export interface DeployStatus {
  pending: { id: string; target: string; status: string; started_at?: number } | null;
  last: { id: string; target: string; ok: boolean; finished_at: number; log?: string } | null;
}
export async function queueDeploy(
  target: "staging" | "production",
): Promise<{ queued: boolean; id?: string; reason?: string }> {
  return api("/api/admin/deploy", { method: "POST", body: { target } });
}
export async function getDeployStatus(): Promise<DeployStatus> {
  return api("/api/admin/deploy-status", { method: "GET" });
}

// §v3.architecture — live overlay for the Mission Control System Architecture map.
export interface ArchChannelStat { configured: boolean; count24h: number }
export interface ArchitectureStatus {
  tenant_id: string;
  health: { shortSha?: string; branch?: string; uptimeSeconds?: number };
  smoke: { ok: boolean; passed: number; total: number; ts?: number } | null;
  channels: Record<string, ArchChannelStat>;
  total24h: number;
}
export async function getArchitectureStatus(tenant?: string): Promise<ArchitectureStatus> {
  return api<ArchitectureStatus>("/api/admin/architecture/status", {
    method: "GET",
    headers: tenant ? { "X-Tenant-ID": tenant } : undefined,
  });
}
export async function getArchitectureTrace(
  conversationId: string,
  tenant?: string,
): Promise<{ conversation_id: string; tenant_id: string; tools: string[] }> {
  return api("/api/admin/architecture/trace?conversation_id=" + encodeURIComponent(conversationId), {
    method: "GET",
    headers: tenant ? { "X-Tenant-ID": tenant } : undefined,
  });
}

// §v3.pw — change your own password (requires the current one).
export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  return api<{ ok: boolean; message: string }>("/api/admin/me/password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

// §v3.security — recent sign-in activity (logbook) for the current operator.
export interface LoginAttempt {
  at: number; // unix seconds
  outcome: string; // "success" | "fail"
  ip: string;
}
export async function loginActivity(): Promise<{ attempts: LoginAttempt[] }> {
  return api<{ attempts: LoginAttempt[] }>("/api/admin/me/login-activity", {
    method: "GET",
  });
}

export async function logout(): Promise<void> {
  try {
    await api("/api/admin/logout", { method: "POST", body: {} });
  } finally {
    clearSession();
  }
}

/**
 * §8.1 — per-tenant voice-model override.
 *
 * `realtime_model` is the EFFECTIVE model (override if set, else
 * the backend's `default_model`). `allowed_models` is the
 * whitelist the Settings dropdown renders; Phase 9 will add
 * Gemini there.
 */
export interface VoiceSettingsResponse {
  realtime_model: string;
  updated_at: number | null;
  updated_by: string | null;
  default_model: string;
  allowed_models: string[];
}

export async function getVoiceSettings(): Promise<VoiceSettingsResponse> {
  return api<VoiceSettingsResponse>("/api/settings/voice");
}

export async function putVoiceSettings(body: {
  realtime_model: string;
}): Promise<VoiceSettingsResponse> {
  return api<VoiceSettingsResponse>("/api/settings/voice", {
    method: "PUT",
    body,
  });
}

// §security.settings — per-tenant agent money-action guardrail toggles. The
// voice gates read these LIVE, so a change takes effect on the next order /
// payment with no restart.
export interface SecuritySettingsResponse {
  tenant_id: string;
  order_confirm_items: number;
  payment_strict_recipient: boolean;
  mfa_required: boolean;
  configured: boolean;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getSecuritySettings(): Promise<SecuritySettingsResponse> {
  return api<SecuritySettingsResponse>("/api/settings/security");
}

export async function putSecuritySettings(body: {
  order_confirm_items?: number;
  payment_strict_recipient?: boolean;
  mfa_required?: boolean;
}): Promise<SecuritySettingsResponse> {
  return api<SecuritySettingsResponse>("/api/settings/security", {
    method: "PUT",
    body,
  });
}

// §stock-toggle (2026-08-07, Akil) — may the agent answer stock questions?
// The switch controls DATA, not permission to guess: "on" lets search results
// carry the live stock field and the prompt reads it back as-is; "off" strips
// it and the agent offers to check with the team; "auto" (default) is on only
// when the operator actually maintains stock counts. Read LIVE — no restart.
export type StockAnswersMode = "auto" | "on" | "off";

export interface StockSettingsResponse {
  mode: StockAnswersMode;
  effective_enabled: boolean;
}

export async function getStockSettings(): Promise<StockSettingsResponse> {
  return api<StockSettingsResponse>("/api/settings/stock");
}

export async function putStockSettings(
  mode: StockAnswersMode,
): Promise<StockSettingsResponse> {
  return api<StockSettingsResponse>("/api/settings/stock", {
    method: "PUT",
    body: { mode },
  });
}

// §security.audit-log (2026-06-20) — read-only operator/security audit trail
// (logins + failures, role/permission changes, MFA, GDPR erasures, master-
// impersonation, security-settings edits) + a tamper-evidence check of the
// per-tenant hash-chain. Both endpoints gated server-side on audit_log.view.
export interface AuditEvent {
  audit_id: string;
  actor: string;
  action: string;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown>;
  ip: string;
  user_agent: string;
  created_at: number; // unix seconds
}

export async function getAuditLog(opts?: {
  limit?: number;
  action?: string;
}): Promise<{ events: AuditEvent[]; count: number }> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.action) params.set("action", opts.action);
  const qs = params.toString();
  return api<{ events: AuditEvent[]; count: number }>(
    `/api/admin/audit-log${qs ? `?${qs}` : ""}`,
  );
}

export interface AuditChainVerdict {
  ok: boolean;
  checked: number;
  broken_at: string | null;
  reason: string;
  legacy: number;
}

export async function verifyAuditChain(): Promise<AuditChainVerdict> {
  return api<AuditChainVerdict>("/api/admin/audit-log/verify");
}

export interface AuditExportBundle {
  tenant_id: string;
  count: number;
  min_seq: number;
  max_seq: number;
  events: Array<Record<string, unknown>>;
  signature: string;
  algo: string;
}

export async function exportAuditLog(): Promise<AuditExportBundle> {
  return api<AuditExportBundle>("/api/admin/audit-log/export");
}

// §agent-qa (2026-06-23) — Agent Reliability / QA dashboard.
export interface AgentEvalTurn {
  user: string;
  reply_text?: string;
  flow_type?: string;
  submit_order?: boolean;
  submit_support?: boolean;
}

export interface AgentEvalCase {
  id: string;
  dim: string;
  check: string;
  scored: boolean;
  passed: boolean | null; // null = informational (not auto-graded)
  evidence?: string;
  note?: string;
  // §proof-sheet (2026-08-08) — the FULL conversation has been persisted with
  // every run since day one (agent_eval.py results_json); the UI just never
  // declared the field. Surfacing it is what turns a score into proof.
  turns?: AgentEvalTurn[];
}
export interface AgentEvalResults {
  score: number;
  total_cases: number;
  scored_cases: number;
  informational_cases: number;
  passed: number;
  failed: number;
  by_dim: Record<string, { passed: number; total: number; label: string }>;
  cases: AgentEvalCase[];
}
export interface AgentEvalRun {
  id: string;
  status: string; // running | completed | failed
  run_type: string;
  started_at: number;
  completed_at: number;
  score: number;
  total_cases: number;
  scored_cases: number;
  passed: number;
  failed: number;
  created_by: string;
  error?: string;
  results?: AgentEvalResults;
}

export async function getEvalLatest(): Promise<{ ok: boolean; run: AgentEvalRun | null }> {
  return api<{ ok: boolean; run: AgentEvalRun | null }>("/api/admin/eval/latest");
}
export async function getEvalRuns(): Promise<{ ok: boolean; runs: AgentEvalRun[] }> {
  return api<{ ok: boolean; runs: AgentEvalRun[] }>("/api/admin/eval/runs");
}
export async function startEvalRun(): Promise<{ ok: boolean; started: boolean }> {
  return api<{ ok: boolean; started: boolean }>("/api/admin/eval/run", {
    method: "POST",
    body: {},
  });
}

/**
 * §13.2 — Telegram integration settings.
 *
 * GET returns the tenant's config with bot_token + webhook_secret
 * redacted to the last 4 chars (so the UI can confirm "a token is
 * saved" without re-exposing it in DevTools / screencaps). PUT
 * is a partial update — send only the fields you're changing;
 * omitted fields keep their existing values. Pass the empty
 * string to explicitly clear a field.
 */
export interface TelegramSettingsResponse {
  tenant_id: string;
  configured: boolean;
  bot_token_redacted: string;
  webhook_secret_redacted: string;
  webhook_url: string;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getTelegramSettings(): Promise<TelegramSettingsResponse> {
  return api<TelegramSettingsResponse>("/api/settings/telegram");
}

export async function putTelegramSettings(body: {
  bot_token?: string;
  webhook_secret?: string;
  webhook_url?: string;
}): Promise<TelegramSettingsResponse> {
  return api<TelegramSettingsResponse>("/api/settings/telegram", {
    method: "PUT",
    body,
  });
}

/**
 * §13.2 — one-shot helper: tell Telegram to deliver updates to
 * the saved webhook URL with the saved secret. Returns
 * Telegram's own ok/description text so the UI can surface
 * "Webhook was set" vs. "Failed: <reason>" inline instead of
 * making operators check a log file.
 */
export interface TelegramSetupWebhookResponse {
  tenant_id: string;
  url: string;
  secret_configured: boolean;
  ok: boolean;
  description: string;
}

export async function setupTelegramWebhook(): Promise<TelegramSetupWebhookResponse> {
  return api<TelegramSetupWebhookResponse>(
    "/api/admin/telegram/setup-webhook",
    { method: "POST", body: {} },
  );
}

/**
 * §13.3 — WhatsApp Cloud API integration settings.
 *
 * Same GET/PUT partial-update shape as Telegram. The backend
 * stores four secrets (phone_number_id, access_token,
 * verify_token, app_secret) in tenant_settings.data_json. All
 * four are redacted on GET to the last 4 chars. On first save
 * of phone_number_id + access_token together, the backend
 * auto-generates a verify_token so the operator can paste it
 * straight into the Meta dashboard.
 *
 * Unlike Telegram there's no setWebhook helper — Meta webhooks
 * are registered in the Meta App dashboard, not via API. The
 * admin form just stores the secrets; pointing Meta at our URL
 * is a manual dashboard step documented inline on the Settings
 * page.
 */
export interface WhatsAppSettingsResponse {
  tenant_id: string;
  configured: boolean;
  phone_number_id_redacted: string;
  access_token_redacted: string;
  // Verify token is a non-secret handshake nonce — returned in full so the
  // operator can copy it into Meta's webhook config.
  verify_token: string;
  verify_token_redacted: string;
  app_secret_redacted: string;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getWhatsAppSettings(): Promise<WhatsAppSettingsResponse> {
  return api<WhatsAppSettingsResponse>("/api/settings/whatsapp");
}

export async function putWhatsAppSettings(body: {
  phone_number_id?: string;
  access_token?: string;
  verify_token?: string;
  app_secret?: string;
}): Promise<WhatsAppSettingsResponse> {
  return api<WhatsAppSettingsResponse>("/api/settings/whatsapp", {
    method: "PUT",
    body,
  });
}

// §onboarding-channels — web-chat widget settings (Settings › Channels). The
// api_key is the PUBLIC widget key that ships inside the embed snippet on the
// tenant's own site, so it's returned in full (nothing to redact).
export interface WebSettingsResponse {
  tenant_id: string;
  configured: boolean;
  enabled: boolean;
  api_key: string;
  snippet: string;
  widget_url: string;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getWebSettings(): Promise<WebSettingsResponse> {
  return api<WebSettingsResponse>("/api/settings/web");
}

/** `enabled` toggles the public ingress; `rotate_key` mints a fresh widget key
 * (the old snippet stops working — re-paste after rotating). */
export async function putWebSettings(body: {
  enabled?: boolean;
  rotate_key?: boolean;
}): Promise<WebSettingsResponse> {
  return api<WebSettingsResponse>("/api/settings/web", { method: "PUT", body });
}

// §crm-connectors — per-tenant CRM integration (Settings › Integrations).
// Secrets come back REDACTED (last-4); echoing a redaction on PUT keeps the
// stored secret. `webhook` = signed outbound POST (Zapier/Make/n8n/anything).
export type CrmProvider =
  | "" | "zoho" | "hubspot" | "pipedrive" | "freshsales" | "bitrix24" | "salesforce" | "webhook";

export interface CrmSettingsResponse {
  provider: CrmProvider;
  enabled: boolean;
  push_leads: boolean;
  configured: boolean;
  creds: Record<string, string>;
  providers: string[];
  updated_at: number | null;
  updated_by: string | null;
}

export async function getCrmSettings(): Promise<CrmSettingsResponse> {
  return api<CrmSettingsResponse>("/api/settings/crm");
}

export async function putCrmSettings(body: {
  provider?: CrmProvider;
  creds?: Record<string, string>;
  enabled?: boolean;
  push_leads?: boolean;
}): Promise<CrmSettingsResponse> {
  return api<CrmSettingsResponse>("/api/settings/crm", { method: "PUT", body });
}

/** Pushes a clearly-labelled TEST lead through the real provider path. */
export async function testCrmConnection(): Promise<{ ok: boolean; detail: string }> {
  return api<{ ok: boolean; detail: string }>("/api/settings/crm/test", { method: "POST", body: {} });
}

// §sip-carrier — Cloud number (Twilio / SIP trunk) settings. Routing is armed
// on our side once the DID is registered; a real inbound call proves the
// carrier side (status stays "armed" until then — never claim "live" from
// config alone).
export interface VoiceTrunkResponse {
  did: string;
  enabled: boolean;
  configured: boolean;
  sip_ingress_uri: string;
  sip_user: string;
  status: "off" | "armed";
}

export async function getVoiceTrunk(): Promise<VoiceTrunkResponse> {
  return api<VoiceTrunkResponse>("/api/settings/voice-trunk");
}

export async function setVoiceTrunkEnabled(enabled: boolean): Promise<VoiceTrunkResponse> {
  return api<VoiceTrunkResponse>("/api/settings/voice-trunk", { method: "PUT", body: { enabled } });
}

/** Register (or re-register) the tenant's Twilio/carrier DID → arms DID→tenant
 * routing. Returns the SIP ingress + call_ready (false until a real call). */
export async function connectVoiceTrunk(trunkDid: string): Promise<{
  ok: boolean; did?: string; sip_ingress_uri?: string; sip_user?: string;
  call_ready?: boolean; routing_ready?: boolean; note?: string;
}> {
  return api("/api/admin/channels/voice/connect", { method: "POST", body: { trunk_did: trunkDid } });
}

// §v3.instagram (2026-05-25) — per-tenant Instagram DM settings. Mirrors the
// WhatsApp shape: a `configured` bool + redacted previews. The access token is
// the only required field (enough to reply to a DM); verify_token is auto-
// generated on first save for pasting into the Meta webhook config.
export interface InstagramSettingsResponse {
  tenant_id: string;
  configured: boolean;
  access_token_redacted: string;
  app_secret_redacted: string;
  verify_token_redacted: string;
  ig_id: string;
  // §v3.instagram-comments — keyword-triggered comment-to-DM (non-secret).
  comment_keywords: string;
  comment_reply_template: string;
  // §v3.instagram-comments-intelligent — agent-generated comment replies.
  intelligent_comments: boolean;
  comment_reply_mode: string; // "public" | "dm" | "both"
  updated_at: number | null;
  updated_by: string | null;
}

export async function getInstagramSettings(): Promise<InstagramSettingsResponse> {
  return api<InstagramSettingsResponse>("/api/settings/instagram");
}

export async function putInstagramSettings(body: {
  access_token?: string;
  app_secret?: string;
  verify_token?: string;
  ig_id?: string;
  comment_keywords?: string;
  comment_reply_template?: string;
  intelligent_comments?: boolean;
  comment_reply_mode?: string;
}): Promise<InstagramSettingsResponse> {
  return api<InstagramSettingsResponse>("/api/settings/instagram", {
    method: "PUT",
    body,
  });
}

// §v3.marketing-send (2026-05-25) — outbound campaign pipeline (increment 6).
export type MarketingChannel = "wa" | "tg" | "em";
export interface MarketingCampaign {
  id: string;
  tenant_id: string;
  name: string;
  channel: MarketingChannel;
  status: "draft" | "scheduled" | "sending" | "sent" | "failed" | "cancelled";
  audience: Record<string, unknown>;
  content: Record<string, unknown>;
  scheduled_at: string;
  created_at: string;
  created_by: string;
  sent_at: string;
  metrics: Record<string, unknown>;
  send_counts?: Record<string, number>;
}

export interface MarketingReach {
  channel: string;
  reachable: number;
  configured: boolean;
}

export async function marketingReach(channel: MarketingChannel): Promise<MarketingReach> {
  return api<MarketingReach>(`/api/marketing/reach?channel=${channel}`);
}

/** Opted-in recipient keys for a channel (tg=chat_id, wa=phone, em=email),
 *  so the Audience builder can narrow a segment to reachable customers. */
export async function marketingOptedInKeys(
  channel: MarketingChannel,
): Promise<{ channel: string; keys: string[] }> {
  return api<{ channel: string; keys: string[] }>(`/api/marketing/opted-in-keys?channel=${channel}`);
}

// §v3.ai-campaign — turn a one-line goal into a ready-to-REVIEW campaign draft
// (on-brand copy + a suggested audience). Drafts only — nothing is sent here.
export interface GeneratedCampaign {
  generated: boolean;
  name: string;
  channel: MarketingChannel;
  content: { body: string; subject?: string; preheader?: string };
  audience: { key: string; label: string; reason: string };
  rationale: string;
  note?: string;
}

export async function generateMarketingCampaign(
  prompt: string,
  channel: MarketingChannel,
): Promise<GeneratedCampaign> {
  return api<GeneratedCampaign>("/api/marketing/generate-campaign", {
    method: "POST",
    body: { prompt, channel },
  });
}

// §v3.segments — smart lifecycle segments computed from real order history.
export interface LifecycleSegment {
  key: string;            // all | recent | at_risk | lapsed | vip
  label: string;
  desc: string;
  total: number;          // customers in this segment
  reachable_wa: number;   // of those, how many are opted in on WhatsApp
  reachable_tg: number;   // …and on Telegram (via the phone↔chat_id identity index)
}
export interface LifecycleSegmentsResponse {
  segments: LifecycleSegment[];
  channels_supported: string[];
  note: string;
}
export async function fetchLifecycleSegments(): Promise<LifecycleSegmentsResponse> {
  return api<LifecycleSegmentsResponse>("/api/marketing/lifecycle-segments");
}

// §v3.audience-saved-segments — reusable audience filter definitions.
export interface MarketingSegment {
  id: string;
  tenant_id: string;
  name: string;
  filters: Record<string, unknown>;
  created_at: string;
  created_by: string;
}
export async function listMarketingSegments(): Promise<{ segments: MarketingSegment[] }> {
  return api<{ segments: MarketingSegment[] }>("/api/marketing/segments");
}
export async function createMarketingSegment(body: {
  name: string;
  filters: Record<string, unknown>;
}): Promise<MarketingSegment> {
  return api<MarketingSegment>("/api/marketing/segments", { method: "POST", body });
}
export async function deleteMarketingSegment(id: string): Promise<{ ok: boolean; id: string }> {
  return api<{ ok: boolean; id: string }>(`/api/marketing/segments/${id}`, { method: "DELETE" });
}

// §v3.automations — trigger-based recurring sends (e.g. win-back).
export interface MarketingAutomation {
  id: string;
  tenant_id: string;
  name: string;
  trigger_type: string;
  trigger_days: number;
  channel: MarketingChannel;
  content: Record<string, unknown>;
  enabled: boolean;
  sent_count: number;
  last_run_at: string;
  created_at: string;
  created_by: string;
}
export async function listMarketingAutomations(): Promise<{ automations: MarketingAutomation[] }> {
  return api<{ automations: MarketingAutomation[] }>("/api/marketing/automations");
}
export async function createMarketingAutomation(body: {
  name: string;
  channel: MarketingChannel;
  trigger_type?: string;
  trigger_days: number;
  content: Record<string, unknown>;
  enabled?: boolean;
}): Promise<MarketingAutomation> {
  return api<MarketingAutomation>("/api/marketing/automations", { method: "POST", body });
}
export async function toggleMarketingAutomation(id: string, enabled: boolean): Promise<MarketingAutomation> {
  return api<MarketingAutomation>(`/api/marketing/automations/${id}`, { method: "PATCH", body: { enabled } });
}
export async function deleteMarketingAutomation(id: string): Promise<{ ok: boolean; id: string }> {
  return api<{ ok: boolean; id: string }>(`/api/marketing/automations/${id}`, { method: "DELETE" });
}
export async function runMarketingAutomation(
  id: string,
  dryRun = true,
): Promise<{ automation_id: string; candidates?: number; would_send?: number; sent?: number; skipped?: string; error?: string }> {
  return api(`/api/marketing/automations/${id}/run${dryRun ? "?dry_run=1" : ""}`, { method: "POST" });
}

export async function listMarketingCampaigns(): Promise<{ campaigns: MarketingCampaign[] }> {
  return api<{ campaigns: MarketingCampaign[] }>("/api/marketing/campaigns");
}

export interface MarketingOverview {
  days: number;
  messages_sent: number;
  prev_messages_sent: number;
  delivered: number;
  read: number;
  replied: number;
  ordered: number;
  failed: number;
  skipped: number;
  campaigns_total: number;
  by_day: Array<{ d: string; n: number }>;
  by_status: Record<string, number>;
}

export async function marketingOverview(days: 7 | 30 | 90 = 30): Promise<MarketingOverview> {
  return api<MarketingOverview>(`/api/marketing/overview?days=${days}`);
}

export async function createMarketingCampaign(body: {
  name: string;
  channel: MarketingChannel;
  content?: Record<string, unknown>;
  audience?: Record<string, unknown>;
  scheduled_at?: string;
  status?: string;
}): Promise<MarketingCampaign> {
  return api<MarketingCampaign>("/api/marketing/campaigns", { method: "POST", body });
}

export async function sendMarketingCampaign(
  id: string,
  opts?: { dry_run?: boolean },
): Promise<{ dry_run?: boolean; status?: string; summary?: Record<string, number> }> {
  return api(`/api/marketing/campaigns/${id}/send`, { method: "POST", body: opts ?? {} });
}

// §v3.marketing-send — email-ESP + approved-WhatsApp-template config.
export interface MarketingWaTemplate {
  name: string;
  category?: string;
  lang?: string;
}
export interface MarketingSettingsResponse {
  tenant_id: string;
  email_configured: boolean;
  esp_provider: string;
  esp_api_key_redacted: string;
  esp_from_name: string;
  esp_from_email: string;
  esp_sending_domain: string;
  esp_physical_address: string;
  esp_reply_to: string;
  /** §v3.webhook-auth — redacted signing key + whether verification is on. */
  esp_webhook_signing_key_redacted?: string;
  esp_webhook_verified?: boolean;
  wa_templates: MarketingWaTemplate[];
  updated_at: number | null;
  updated_by: string | null;
}

export async function getMarketingSettings(): Promise<MarketingSettingsResponse> {
  return api<MarketingSettingsResponse>("/api/settings/marketing");
}

// §v3.deliverability — live SPF/DMARC/DKIM check of the sending domain.
export interface MarketingDnsRecord {
  present: boolean;
  value?: string;
  selector?: string;
  hint?: string;
}
export interface MarketingDnsCheck {
  domain: string;
  available: boolean;
  ok: boolean;
  error?: string;
  spf?: MarketingDnsRecord;
  dmarc?: MarketingDnsRecord;
  dkim?: MarketingDnsRecord;
}
export async function marketingDnsCheck(): Promise<MarketingDnsCheck> {
  return api<MarketingDnsCheck>("/api/settings/marketing/dns-check");
}

export async function putMarketingSettings(body: {
  esp_provider?: string;
  esp_api_key?: string;
  esp_from_name?: string;
  esp_from_email?: string;
  esp_sending_domain?: string;
  esp_physical_address?: string;
  esp_reply_to?: string;
  esp_webhook_signing_key?: string;
  wa_templates?: MarketingWaTemplate[];
}): Promise<MarketingSettingsResponse> {
  return api<MarketingSettingsResponse>("/api/settings/marketing", {
    method: "PUT",
    body,
  });
}

// §v3.multi-tenant (2026-06-02) — public voice widget opt-in. The public
// /call page + Telegram Mini App are unauthenticated and spend this tenant's
// xAI credits, so a tenant must opt in before its agent is exposed publicly.
export interface PublicWidgetResponse {
  tenant_id: string;
  enabled: boolean;
  public_path: string;
}

export async function getPublicWidget(): Promise<PublicWidgetResponse> {
  return api<PublicWidgetResponse>("/api/settings/public-widget");
}

export async function putPublicWidget(
  enabled: boolean,
): Promise<PublicWidgetResponse> {
  return api<PublicWidgetResponse>("/api/settings/public-widget", {
    method: "PUT",
    body: { enabled },
  });
}

/**
 * §13.5d — UCMRC (Grandstream Cloud PBX) bridge config.
 *
 * Separate namespace from `/api/settings/sip` because UCMRC is
 * the user's existing on-prem PBX (0721ae.s.myucm.cloud for
 * Roze). Rosie registers AS an extension (e.g. 104) on the PBX;
 * staff dial that extension from their Wave softphones.
 *
 * Host is redacted to last-8 (not last-4 like the password) —
 * the tenant-specific subdomain can identify the customer to
 * anyone who screencaps the admin page, so it gets the same
 * redaction treatment as secrets. `allowed_transports` is
 * returned server-side so the frontend dropdown renders from
 * one source of truth.
 */
export type UcmrcTransport = "udp" | "tcp" | "tls";

export interface UcmrcSettingsResponse {
  tenant_id: string;
  configured: boolean;
  host_redacted: string;
  port: string;
  extension: string;
  password_redacted: string;
  auth_user: string;
  transport: string;
  inbound_did: string;
  effective_port: number;
  effective_transport: UcmrcTransport;
  effective_auth_user: string;
  allowed_transports: UcmrcTransport[];
  updated_at: number | null;
  updated_by: string | null;
}

export async function getUcmrcSettings(): Promise<UcmrcSettingsResponse> {
  return api<UcmrcSettingsResponse>("/api/settings/ucmrc");
}

export async function putUcmrcSettings(body: {
  host?: string;
  port?: string;
  extension?: string;
  password?: string;
  auth_user?: string;
  transport?: UcmrcTransport | "";
  inbound_did?: string;
}): Promise<UcmrcSettingsResponse> {
  return api<UcmrcSettingsResponse>("/api/settings/ucmrc", {
    method: "PUT",
    body,
  });
}

export interface UcmrcTestResult {
  reachable: boolean;
  not_configured?: boolean;
  host_redacted?: string;
  port?: number;
  transport?: string;
  error?: string;
}

export async function testUcmrcConnection(): Promise<UcmrcTestResult> {
  return api<UcmrcTestResult>("/api/settings/ucmrc/test");
}

/**
 * §13.5b — AI provider API keys (OpenAI + Gemini).
 *
 * §gemini-at-will (2026-06-21) — Gemini-only now (OpenAI retired; the xAI key
 * has its own surface). Per-tenant storage via /api/settings/api-keys with
 * last-4 redaction. `has_gemini` reports whether a DB value is saved;
 * `gemini_env_fallback` flags a deployment-level GEMINI_API_KEY/GOOGLE_API_KEY
 * so the UI can show "env override active" instead of "not configured".
 *
 * Backend resolver (`api_keys_settings.resolve_gemini_key`) prefers the DB
 * value, falling back to env. Rotates instantly — the next chat turn (after a
 * provider flip) uses the new value without a backend restart.
 */
export interface ApiKeysResponse {
  tenant_id: string;
  has_gemini: boolean;
  gemini_key_redacted: string;
  gemini_env_fallback: boolean;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getApiKeys(): Promise<ApiKeysResponse> {
  return api<ApiKeysResponse>("/api/settings/api-keys");
}

export async function putApiKeys(body: {
  gemini_key?: string;
}): Promise<ApiKeysResponse> {
  return api<ApiKeysResponse>("/api/settings/api-keys", {
    method: "PUT",
    body,
  });
}

/**
 * §gemini-at-will (2026-06-21) — per-tenant chat-provider toggle (xAI ↔ Gemini).
 *
 * The PUT persists the choice + invalidates the backend's cached XpandBrain (and
 * cross-process via the cache bus) so the next text chat turn (Telegram /
 * WhatsApp / dashboard) picks up the new provider without a restart. If Gemini
 * is selected without a resolvable key, the brain soft-falls-back to xAI.
 */
// §gpt54-chat (2026-07-09) — "openai" restored (gpt-5.4-mini chat backend).
export type ChatProvider = "xai" | "gemini" | "openai";

export interface ChatProviderResponse {
  tenant_id: string;
  provider: ChatProvider;
  allowed: ChatProvider[];
  default: ChatProvider;
  configured: boolean;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getChatProvider(): Promise<ChatProviderResponse> {
  return api<ChatProviderResponse>("/api/settings/chat-provider");
}

export async function putChatProvider(
  provider: ChatProvider,
): Promise<ChatProviderResponse> {
  return api<ChatProviderResponse>("/api/settings/chat-provider", {
    method: "PUT",
    body: { provider },
  });
}

/**
 * §v3.chat-model-picker — per-tenant chat MODEL override. Sister to
 * the chat-provider endpoint above. Lets each tenant pick which
 * OpenAI model (e.g. gpt-4.1-mini vs gpt-4.1 vs gpt-5) and which
 * Gemini model (e.g. gemini-2.5-flash vs gemini-3.1-flash) the
 * brain uses for chat, independent of the provider choice.
 *
 * Backend: GET + PUT /api/settings/chat-model. PUT body:
 * ``{ openai_model?: string | null, gemini_model?: string | null }``.
 *   * key absent OR value === null → leave that slot untouched
 *   * value === "" → clear (fall back to default)
 *   * non-empty string → must be in the respective allowed list
 * PUT invalidates the cached XpandBrain so the next turn picks
 * the new model without a process restart.
 */
export interface ChatModelResponse {
  tenant_id: string;
  openai_model: string | null;
  gemini_model: string | null;
  default_openai_model: string;
  default_gemini_model: string;
  allowed_openai_models: string[];
  allowed_gemini_models: string[];
  updated_at: number | null;
  updated_by: string | null;
}

export async function getChatModel(): Promise<ChatModelResponse> {
  return api<ChatModelResponse>("/api/settings/chat-model");
}

export async function putChatModel(
  update: {
    openai_model?: string | null;
    gemini_model?: string | null;
  },
): Promise<ChatModelResponse> {
  return api<ChatModelResponse>("/api/settings/chat-model", {
    method: "PUT",
    body: update,
  });
}

/**
 * §5.1-bug2 — consume a first-login magic token minted by the
 * onboarding wizard's Go Live step. The server looks up the tenant
 * by token, bootstraps or reuses the operator account tied to the
 * wizard-captured ``operator_email``, mints a session + JWT pair
 * matching the shape of a regular login, and clears the token so
 * the link is one-shot.
 */
export async function magicLogin(
  token: string,
): Promise<{ needsPasswordSet: boolean }> {
  const data = await api<
    Awaited<ReturnType<typeof api>> & {
      tenant_id?: string;
      needs_password_set?: boolean;
    }
  >("/api/admin/magic-login", {
    method: "POST",
    auth: false,
    body: { token },
  });
  persistLogin(data as Parameters<typeof persistLogin>[0]);
  // §5.1-bug4: the magic-login response binds the operator to a
  // specific tenant. Store it so subsequent admin requests send
  // `X-Tenant-ID: <this-tenant>` instead of defaulting to "roze".
  if (typeof data === "object" && data && "tenant_id" in data) {
    const tenantId = String((data as { tenant_id?: string }).tenant_id || "");
    if (tenantId) setTenantId(tenantId);
  }
  // §onboarding-activation (decision #3) — a freshly-bootstrapped Owner is on
  // a random password; the caller routes them to the set-password screen.
  return {
    needsPasswordSet: Boolean(
      (data as { needs_password_set?: boolean }).needs_password_set,
    ),
  };
}

/**
 * §onboarding-activation (decision #3) — set the Owner's password for the
 * FIRST time after a magic-link login. No current password required, but the
 * server hard-gates this to the bootstrap-password state. The endpoint revokes
 * the old session and returns a fresh token pair, which we persist so the
 * Owner stays signed in.
 */
export async function setInitialPassword(newPassword: string): Promise<void> {
  const data = await api<
    Awaited<ReturnType<typeof api>> & { tenant_id?: string }
  >("/api/admin/me/set-initial-password", {
    method: "POST",
    body: { new_password: newPassword },
  });
  persistLogin(data as Parameters<typeof persistLogin>[0]);
  if (typeof data === "object" && data && "tenant_id" in data) {
    const tenantId = String((data as { tenant_id?: string }).tenant_id || "");
    if (tenantId) setTenantId(tenantId);
  }
}

/**
 * §13.5g — admin surface for orders + support tickets.
 *
 * Shapes mirror the Python ``admin_store`` module. All endpoints
 * are admin-auth'd + tenant-scoped server-side; client calls are
 * read-only GETs for list/get + PATCH for partial updates.
 */

export interface OrderItem {
  name?: string;
  qty?: number;
  /** §v3.order-price-keys (2026-05-13) — items_json uses two key
   * shapes in the wild:
   *   • voice agent + ``sheets.py :: _items_and_total_from_text``
   *     write ``unit_aed`` + ``subtotal_aed`` (canonical since
   *     2026-05-06)
   *   • older drafts + the manual-create drawer use ``price``
   * The frontend's read path uses ``getItemUnitPrice`` /
   * ``getItemLineTotal`` from ``@/lib/orderItemPrice`` which try
   * the agent keys first and fall back to ``price``. Declare all
   * three on the type so TypeScript stops complaining at the
   * callsites. */
  price?: number;
  unit_aed?: number;
  subtotal_aed?: number;
  /** §v3.order-detail — per-line image override served from
   * /api/admin/orders/{id}/line-images/{filename}. Empty
   * string → renders the gradient initial placeholder. */
  image_override_url?: string;
  // Room for future fields (sku, variant) without a schema churn.
  [key: string]: unknown;
}

export interface OrderRow {
  id: number;
  tenant_id: string;
  conversation_id: string | null;
  customer_identifier: string | null;
  channel: string;
  items: OrderItem[];
  items_json: string;
  total_aed: number;
  currency: string;
  status: string;
  /** §b2b — "order" (consumer/confirmed) | "rfq" (B2B quote request). Absent on
   *  legacy rows → treat as "order". The API always sends it post-migration. */
  order_type?: string;
  /** §b2b Stage C — quote/PO fields (populated once an RFQ is quoted). */
  quote_total_aed?: number | null;
  quote_valid_until?: number | null;
  po_number?: string | null;
  lead_time_days?: number | null;
  /** §b2b Stage E — linked corporate account (accounts_store). Null = B2C/unlinked. */
  account_id?: number | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
  // §v3.order-identity-parse (2026-05-06) — flat copies of the
  // identity fields parsed out of notes-JSON on the backend by
  // ``admin_store._orders_row_to_dict``. Always present on rows
  // returned from /api/admin/orders + /api/admin/customers/by-*.
  customer_name?: string;
  customer_phone?: string;
  delivery_address?: string;
  payment_method?: string;
  products?: string;
  quantity?: string;
  /** §v3.customer-code — populated by the by-phone/by-id endpoint
   *  so the timeline / order-detail view can show ``RZ-26050601``
   *  inline without a per-row lookup. Empty on /api/admin/orders
   *  list responses (where customer linkage isn't always known
   *  yet); always populated on /api/admin/customers/by-* responses. */
  customer_code?: string;
  /** §v3.read-time-enrich (2026-05-07) — see the same fields on
   *  ``SupportTicket``. Populated by the backend's ``_enrich_order``
   *  for legacy rows whose ``customer_name`` / ``customer_phone``
   *  parse missed; the frontend prefers these over the empty
   *  notes-JSON values to avoid "Unknown customer" labels. */
  resolved_customer_name?: string;
  resolved_customer_phone?: string;
  resolved_customer_code?: string;
  /** §v3.payments — payment-link status attached by the orders
   *  list/detail endpoints. Absent until a link is created. */
  payment?: OrderPayment;
}

// §v3.payments (2026-05-25) — payment-link record on an order.
export type PaymentStatus =
  | "unpaid"
  // §payments.bank-claim — customer reported a bank transfer; awaiting the
  // merchant's confirmation (received, not yet verified).
  | "claimed"
  | "net_30_invoiced"
  | "paid"
  | "expired"
  | "canceled"
  | "refunded";
export interface OrderPayment {
  status: PaymentStatus;
  link_url: string;
  amount_aed: number;
  currency: string;
  channel: string; // "wa" | "email" | "" | "whatsapp"
  recipient: string;
  provider: string;
  paid_at: string;
  updated_at: string;
  /** §b2b Stage E — credit invoicing metadata (prepaid orders carry defaults). */
  terms?: string;
  due_date?: string;
  invoice_number?: string;
}

export interface OrdersListResponse {
  orders: OrderRow[];
  /** §orders-query — aggregates over the FULL filtered set (the page used
   *  to derive every headline from the visible 100 rows). */
  kpis?: {
    total_count: number;
    today: number;
    open: number;
    by_status: Record<string, number>;
    revenue_by_currency: Record<string, number>;
    sales_count: number;
  } | null;
  total_count?: number;
  allowed_statuses: string[];
  editable_fields: string[];
}

export interface OrdersListFilters {
  status?: string;
  channel?: string;
  /** §b2b — "rfq" isolates B2B quote requests; "order" excludes them. */
  order_type?: string;
  /** §b2b Stage E — all orders for one corporate account. */
  account_id?: number;
  query?: string;
  /** §v3.orders-date-filter (2026-05-11) — INCLUSIVE range
   *  against ``orders.created_at``. Accepts the YYYY-MM-DD
   *  value an ``<input type="date">`` emits. Empty = unbounded. */
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

function buildQueryString(
  obj: Record<string, string | number | undefined | null>,
): string {
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (!entries.length) return "";
  return "?" + new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
}

export async function listOrders(
  filters: OrdersListFilters = {},
): Promise<OrdersListResponse> {
  return api<OrdersListResponse>(
    `/api/admin/orders${buildQueryString(filters as Record<string, string | number | undefined>)}`,
  );
}

export async function getOrder(
  orderId: number,
): Promise<{ order: OrderRow }> {
  return api<{ order: OrderRow }>(`/api/admin/orders/${orderId}`);
}

// ── §v3.payments — payment links on an order ──────────────────────
export interface PaymentLinkResult {
  payment: OrderPayment;
  sent: boolean | null;
  send_error: string | null;
}

/** Create (or refresh) a hosted payment link for an order, optionally
 *  sending it over the chosen channel. */
export async function createOrderPaymentLink(
  orderId: number,
  body: { channel?: "wa" | "email"; recipient?: string; send?: boolean },
): Promise<PaymentLinkResult> {
  return api<PaymentLinkResult>(`/api/admin/orders/${orderId}/payment-link`, {
    method: "POST",
    body,
  });
}

export async function resendOrderPayment(
  orderId: number,
  body: { channel?: "wa" | "email"; recipient?: string },
): Promise<{ sent: boolean; send_error: string | null }> {
  return api(`/api/admin/orders/${orderId}/payment/resend`, { method: "POST", body });
}

export async function markOrderPaid(
  orderId: number,
): Promise<{ payment: OrderPayment }> {
  return api(`/api/admin/orders/${orderId}/payment/mark-paid`, { method: "POST", body: {} });
}

/** §payments.bank-claim — flag an order as "awaiting confirmation": the
 *  customer reported a bank transfer that the business hasn't verified yet. */
export async function markOrderClaimed(
  orderId: number,
): Promise<{ payment: OrderPayment | null }> {
  return api(`/api/admin/orders/${orderId}/payment/mark-claimed`, { method: "POST", body: {} });
}

/** §payments.bank-claim — revert to unpaid ("Not received", or undoing a
 *  mis-mark). A deliberate downgrade; the backend forces past the rank guard. */
export async function markOrderUnpaid(
  orderId: number,
): Promise<{ payment: OrderPayment | null }> {
  return api(`/api/admin/orders/${orderId}/payment/mark-unpaid`, { method: "POST", body: {} });
}

export interface PaymentSettingsResponse {
  tenant_id: string;
  configured: boolean;
  /** §cod-only-toggle — owner switch: false = agent quotes cash on delivery
   *  only, no link is generated, offered, or re-shared on any channel. */
  links_enabled: boolean;
  webhook_ready: boolean;
  provider: string;
  secret_key_redacted: string;
  webhook_secret_redacted: string;
  currency: string;
  updated_at: number | null;
  updated_by: string | null;
}

export async function getPaymentSettings(): Promise<PaymentSettingsResponse> {
  return api<PaymentSettingsResponse>("/api/settings/payments");
}

export async function putPaymentSettings(body: {
  provider?: string;
  secret_key?: string;
  webhook_secret?: string;
  currency?: string;
  links_enabled?: boolean;
}): Promise<PaymentSettingsResponse> {
  return api<PaymentSettingsResponse>("/api/settings/payments", { method: "PUT", body });
}

export async function patchOrder(
  orderId: number,
  patch: Partial<{
    customer_identifier: string;
    channel: string;
    items_json: string | OrderItem[];
    total_aed: number;
    currency: string;
    status: string;
    notes: string;
    /** §b2b Stage E — link/unlink the order to a corporate account (null clears). */
    account_id: number | null;
  }>,
): Promise<{ order: OrderRow }> {
  return api<{ order: OrderRow }>(`/api/admin/orders/${orderId}`, {
    method: "PATCH",
    body: patch,
  });
}

// §v3.manual-create (2026-05-08) — operator manual create flows.
// Body fields mirror the agent's voice_tools.create_order /
// support_ticket primitives, so dashboard-created rows are byte-
// for-byte identical to agent-created ones (notes-JSON shape,
// channel column, customer linkage, status="pending").
export interface CreateOrderBody {
  customer_name: string;
  phone_number?: string;
  products: string;
  quantity?: string;
  delivery_address?: string;
  payment_method?: string;
  notes?: string;
  channel?: string;
}

export async function createOrder(
  body: CreateOrderBody,
): Promise<{ ok: boolean; order_id: string; channel: string }> {
  return api<{ ok: boolean; order_id: string; channel: string }>(
    "/api/admin/orders",
    { method: "POST", body },
  );
}

export interface CreateSupportTicketBody {
  summary: string;
  customer_name?: string;
  phone_number?: string;
  issue_type?: string;
  order_reference?: string;
  priority?: string;
  channel?: string;
}

export async function createSupportTicket(
  body: CreateSupportTicketBody,
): Promise<{ ok: boolean; ticket_id: number | null; priority: string }> {
  return api<{ ok: boolean; ticket_id: number | null; priority: string }>(
    "/api/admin/support-tickets",
    { method: "POST", body },
  );
}

// §v3.bulk-delete (2026-05-13) — operator-initiated multi-row delete
// from the Orders / Support pages. Backend lives at
// /api/admin/{orders,support-tickets}/bulk-delete; returns count of
// rows actually removed (cross-tenant + invalid ids are skipped
// server-side, see admin_store.delete_orders / delete_support_tickets).
export interface BulkDeleteResult {
  requested: number;
  deleted: number;
  deleted_ids: number[];
}

export async function bulkDeleteOrders(
  ids: number[],
): Promise<BulkDeleteResult> {
  return api<BulkDeleteResult>("/api/admin/orders/bulk-delete", {
    method: "POST",
    body: { ids },
  });
}

export async function bulkDeleteSupportTickets(
  ids: number[],
): Promise<BulkDeleteResult> {
  return api<BulkDeleteResult>("/api/admin/support-tickets/bulk-delete", {
    method: "POST",
    body: { ids },
  });
}

export interface CreateCustomerBody {
  display_name?: string;
  phone_e164?: string;
  address?: string;
  telegram_chat_id?: string;
  whatsapp_wa_id?: string;
}

export async function createCustomer(
  body: CreateCustomerBody,
): Promise<{
  ok: boolean;
  customer_id: string;
  display_name: string;
  phone_e164: string;
}> {
  return api<{
    ok: boolean;
    customer_id: string;
    display_name: string;
    phone_e164: string;
  }>("/api/admin/customers", { method: "POST", body });
}

/** §v3.order-detail (2026-05-05) — append-only timeline of
 *  edits on one order. Newest first. Backs the OrderDetail
 *  page's "Order history" sidebar panel. */
export interface OrderHistoryEntry {
  id: number;
  /** Unix epoch seconds. */
  at: number;
  actor: string;
  /** "status_change" | "field_edit" | "note_added" |
   *  "image_override" | "image_clear" — kept open-ended so
   *  the backend can grow new types without breaking the UI. */
  type: string;
  delta: string;
}

export async function getOrderHistory(
  orderId: number,
): Promise<{ history: OrderHistoryEntry[] }> {
  return api<{ history: OrderHistoryEntry[] }>(
    `/api/admin/orders/${orderId}/history`,
  );
}

/** §v3.order-detail — upload a per-line image override. The
 *  uploaded file is stored under the order's id; the SKU's
 *  catalog image_url is NOT touched. Returns the updated order
 *  + the public URL the frontend should render. */
export async function uploadOrderItemImage(
  orderId: number,
  itemIndex: number,
  file: File,
): Promise<{ order: OrderRow; image_url: string }> {
  const fd = new FormData();
  fd.append("file", file);
  return api<{ order: OrderRow; image_url: string }>(
    `/api/admin/orders/${orderId}/items/${itemIndex}/image`,
    { method: "POST", body: fd },
  );
}

export async function clearOrderItemImage(
  orderId: number,
  itemIndex: number,
): Promise<{ order: OrderRow }> {
  return api<{ order: OrderRow }>(
    `/api/admin/orders/${orderId}/items/${itemIndex}/image`,
    { method: "DELETE" },
  );
}

export interface SupportTicket {
  id: number;
  tenant_id: string;
  conversation_id: string | null;
  customer_identifier: string | null;
  channel: string;
  issue_type: string;
  summary: string;
  status: string;
  priority: string;
  created_at: number;
  updated_at: number;
  /** §v3.customer-code — stamped by the by-phone/by-id endpoint
   *  so the activity timeline can show the friendly id inline. */
  customer_code?: string;
  /** §v3.read-time-enrich (2026-05-07) — populated by the backend's
   *  ``_enrich_ticket`` helper at read time. The backend looks up
   *  the customer by phone in unified_history and attaches the
   *  canonical ``display_name`` / ``phone_e164`` / ``customer_code``
   *  here so the frontend modal can render a real name even for
   *  tickets whose row never carried one (legacy data + any future
   *  write path that drops the customer fields). The original
   *  ``customer_identifier`` / ``customer_code`` columns are NOT
   *  overwritten — these are additive fallback fields. */
  resolved_customer_name?: string;
  resolved_customer_phone?: string;
  resolved_customer_code?: string;
}

/** §v3.support-enum — canonical category metadata served with the
 * tickets list response so the frontend filter bar renders a
 * stable, operator-readable dropdown. */
export interface SupportCategoryInfo {
  id: string;
  display_name: string;
  is_urgent: boolean;
  default_priority: "low" | "medium" | "high";
}

export interface TicketsListResponse {
  tickets: SupportTicket[];
  /** §orders-query — full-set aggregates (header numbers used to come from
   *  the visible 100 rows). */
  kpis?: {
    total_count: number;
    open: number;
    high: number;
    today: number;
    resolved_week: number;
    by_status: Record<string, number>;
  } | null;
  total_count?: number;
  allowed_statuses: string[];
  allowed_priorities: string[];
  editable_fields: string[];
  /** Optional so older backends that predate the canonical enum
   * don't break the frontend — the page falls back to deriving
   * options from the loaded tickets. */
  allowed_issue_types?: SupportCategoryInfo[];
}

export interface TicketsListFilters {
  status?: string;
  channel?: string;
  issue_type?: string;
  /** §v3.makeover — the list page filters by priority too; declared here so
   *  the type documents the real filter surface (was only compiling via the
   *  `as Record<...>` cast in listSupportTickets). */
  priority?: string;
  query?: string;
  /** §v3.support-date-filter (2026-05-11) — INCLUSIVE date range.
   *  Accepts the YYYY-MM-DD value an <input type="date"> emits, or
   *  a full ISO-8601 datetime. Empty string = unbounded. */
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export async function listSupportTickets(
  filters: TicketsListFilters = {},
): Promise<TicketsListResponse> {
  return api<TicketsListResponse>(
    `/api/admin/support-tickets${buildQueryString(filters as Record<string, string | number | undefined>)}`,
  );
}

export async function getSupportTicket(
  ticketId: number,
): Promise<{ ticket: SupportTicket }> {
  return api<{ ticket: SupportTicket }>(
    `/api/admin/support-tickets/${ticketId}`,
  );
}

export async function patchSupportTicket(
  ticketId: number,
  patch: Partial<{
    issue_type: string;
    summary: string;
    status: string;
    priority: string;
  }>,
): Promise<{ ticket: SupportTicket }> {
  return api<{ ticket: SupportTicket }>(
    `/api/admin/support-tickets/${ticketId}`,
    { method: "PATCH", body: patch },
  );
}

/**
 * §13.5f — voice registry (male/female + description).
 */
export interface VoiceInfo {
  id: string;
  provider: "openai" | "gemini" | "grok";
  display_name: string;
  gender: "female" | "male" | "neutral";
  description: string;
  recommended_for: string[];
}

export interface VoicesResponse {
  openai: VoiceInfo[];
  gemini: VoiceInfo[];
  /** §v3.grok-voice — returned even when empty so the frontend can
   * treat it uniformly with the other providers. Older backends
   * that haven't been restarted after the Grok rollout may omit
   * the key; consumers should treat `undefined` as `[]`. */
  grok?: VoiceInfo[];
  /** §grok-voices-2026-07 — Grok Voice's OFFICIALLY supported languages
   * (docs.x.ai); every grok voice is natively multilingual across them.
   * Older backends omit the key. */
  grok_languages?: string[];
}

export async function getVoiceRegistry(): Promise<VoicesResponse> {
  return api<VoicesResponse>("/api/settings/voices");
}

export interface VoicePicksResponse {
  tenant_id: string;
  /** §voice-picker-openai — which provider actually drives THIS tenant's
   * live voice, so the picker shows the matching roster + saves the right
   * pick field. "grok" for xAI tenants, "openai" for the realtime pilot.
   * Undefined on older backends → treat as "grok". */
  active_provider?: "openai" | "grok";
  /** §v3.grok-voice — built-in voice pick (ara/eve/rex/sal/leo).
   * Treat undefined as empty (back-compat with an older backend
   * that hasn't yet been restarted). */
  voice_grok?: string;
  /** §v3.custom-voice — cloned xAI voice id (8-char lowercase
   * alphanumeric, from the xAI console). Empty = no custom voice;
   * when set it takes precedence over voice_grok. */
  voice_grok_custom?: string;
  /** §voice-picker-openai — built-in OpenAI realtime voice
   * (marin/cedar/coral/…). Empty = deployment default. */
  voice_openai?: string;
}

export async function getVoicePicks(): Promise<VoicePicksResponse> {
  return api<VoicePicksResponse>("/api/settings/voice-picks");
}

export async function putVoicePicks(
  picks: Partial<{
    voice_grok: string;
    voice_grok_custom: string;
    voice_openai: string;
  }>,
): Promise<VoicePicksResponse> {
  return api<VoicePicksResponse>("/api/settings/voice-picks", {
    method: "PUT",
    body: picks,
  });
}

/** §chat-lifecycle — per-tenant chat-session lifecycle knobs
 * (Agent Studio → Persona → Live chat monitor). */
export interface ChatLifecycleResponse {
  tenant_id?: string;
  /** Minutes a quiet chat stays on the Live Monitor before aging off. */
  live_window_minutes: number;
  /** Close the monitor session immediately when the chat produces an
   * order or a support ticket (nothing left to supervise). */
  close_on_outcome: boolean;
  /** Minutes after the agent's last unanswered message before the
   * abandoned-chat follow-up nudge fires. */
  followup_delay_minutes: number;
  /** Hours of prior context replayed into the Live Monitor when a
   * returning customer reopens a session. 0 = never replay. */
  history_replay_hours: number;
}

export async function getChatLifecycle(): Promise<ChatLifecycleResponse> {
  return api<ChatLifecycleResponse>("/api/settings/chat-lifecycle");
}

export async function putChatLifecycle(
  patch: Partial<{
    live_window_minutes: number;
    followup_delay_minutes: number;
    close_on_outcome: boolean;
    history_replay_hours: number;
  }>,
): Promise<ChatLifecycleResponse> {
  return api<ChatLifecycleResponse>("/api/settings/chat-lifecycle", {
    method: "PUT",
    body: patch,
  });
}

/** §v3.voice-preview — fetch a one-shot audio preview of a voice
 * and return an object URL the browser can play.
 *
 * Why not just return a plain URL the caller hands to ``new Audio()``?
 * Because the admin API is JWT-Bearer-gated — the same reason
 * `apiFetchBlob` exists for call recordings. An ``<audio src=>``
 * can't send custom headers, and the ``?secret=`` query-param
 * approach used by CSV downloads doesn't work for the admin routes
 * (the JWT middleware rejects anything without ``Authorization:
 * Bearer ...``). So we go through the normal authed fetch path,
 * read the body as a Blob, and hand back an ``object URL`` that
 * ``<audio>`` / ``new Audio()`` can consume directly.
 *
 * The caller is responsible for calling ``URL.revokeObjectURL`` on
 * the returned URL when it's no longer needed — same contract as
 * the recording helper in ``useCallRecording``.
 */
/** §settings-email-card (2026-07-10) — tenant transactional email (Gmail/
 * SMTP app-password). Backend: /api/settings/email GET/PUT + /test. The
 * smtp_password echo is REDACTED server-side; sending a non-empty value
 * replaces it, empty string leaves it unchanged (partial update). */
export interface EmailSettingsView {
  enabled?: boolean;
  from_name?: string;
  reply_to?: string;
  own_smtp?: boolean;
  smtp_host?: string;
  smtp_port?: string;
  smtp_user?: string;
  /** redacted echo, e.g. "abcd************" — never the real secret */
  smtp_password?: string;
  smtp_from?: string;
  configured?: boolean;
}

export async function getEmailSettings(): Promise<EmailSettingsView> {
  return api<EmailSettingsView>("/api/settings/email");
}

export async function putEmailSettings(
  patch: Partial<EmailSettingsView>,
): Promise<EmailSettingsView> {
  return api<EmailSettingsView>("/api/settings/email", {
    method: "PUT",
    body: patch,
  });
}

export async function testEmailSettings(
  to: string,
): Promise<{ ok: boolean; error?: string; detail?: string }> {
  return api<{ ok: boolean; error?: string; detail?: string }>(
    "/api/settings/email/test",
    { method: "POST", body: { to } },
  );
}

/** §customer-edit (2026-07-10) — operator edit of a customer's identity
 * from the 360 page. Name/address are stored as overrides (order history
 * is never rewritten); a phone change re-keys the identity and moves the
 * orders with it. Fires an operator push notification server-side. */
export async function patchCustomer(body: {
  phone: string;
  display_name?: string;
  address?: string;
  new_phone?: string;
}): Promise<{ ok: boolean; changed: Record<string, string> }> {
  return api<{ ok: boolean; changed: Record<string, string> }>(
    "/api/admin/customers",
    { method: "PATCH", body },
  );
}

export async function fetchVoicePreview(
  provider: "openai" | "gemini" | "grok",
  voiceId: string,
  // §v3.preview-greeting — the tenant's opening line, so the sample speaks the
  // REAL greeting in each voice (matches the onboarding picker). Optional;
  // blank falls back to a neutral sample line server-side.
  text?: string,
): Promise<string> {
  const params = new URLSearchParams({ provider, voice: voiceId });
  if (text && text.trim()) params.set("text", text.trim().slice(0, 200));
  const blob = await apiFetchBlob(
    `/api/admin/voices/preview?${params.toString()}`,
    { method: "GET" },
  );
  return URL.createObjectURL(blob);
}

/**
 * §13.5i — customer profile lookup.
 *
 * Returns everything known about one customer keyed by phone
 * number: their identity record (remembered address, channel
 * IDs), conversation history across every channel, and their
 * placed orders + support tickets.
 */

export interface CustomerRecord {
  id: string;
  tenant_id: string;
  display_name: string;
  phone_e164: string;
  telegram_chat_id: string;
  telegram_user_id: string;
  whatsapp_wa_id: string;
  desktop_user_id: string;
  address: string;
  metadata_json: string;
  created_at: string;
  updated_at: string;
  /** §v3.customer-code (2026-05-06) — friendly tenant-scoped id
   *  (e.g. ``RZ-26050601``) populated by the by-phone/by-id
   *  endpoint. Empty on legacy responses. */
  customer_code?: string;
  /** §v3.customers-filters (2026-05-06) — manual classifier set
   *  via the Customer 360 Identity panel. Stored in the customer's
   *  ``metadata_json["gender"]`` blob. Defaults to ``"unknown"``
   *  when the operator hasn't set it. */
  gender?: "male" | "female" | "unknown";
}

export interface CustomerConversation {
  id: string;
  tenant_id: string;
  channel: string;
  platform_conversation_id: string;
  customer_id: string;
  endpoint_id: string;
  title: string;
  status: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  /** §v3.preview-text — first user message text, joined by the
   *  backend so the timeline has something meaningful to render
   *  when summary_text was never populated. */
  preview_text?: string;
  message_count?: number;
  sentiment_label?: string;
  summary_text?: string;
  primary_recording_asset_id?: string;
  [key: string]: unknown;
}

export interface CustomerProfileResponse {
  phone: string;
  customer: CustomerRecord | null;
  conversations: CustomerConversation[];
  orders: OrderRow[];
  support_tickets: SupportTicket[];
}

export async function getCustomerByPhone(
  phone: string,
): Promise<CustomerProfileResponse> {
  return api<CustomerProfileResponse>(
    `/api/admin/customers/by-phone?phone=${encodeURIComponent(phone)}`,
  );
}

/** §v3.customers-list (2026-05-05) — load the full Customer 360
 *  bundle by the customer's UUID. Used when the operator clicks a
 *  row in the browse list (where we already have ``customer.id``)
 *  rather than typing a phone number. The response shape matches
 *  ``getCustomerByPhone`` so the page can swap helpers freely. */
export async function getCustomerById(
  id: string,
): Promise<CustomerProfileResponse> {
  return api<CustomerProfileResponse>(
    `/api/admin/customers/by-id?id=${encodeURIComponent(id)}`,
  );
}

/** §v3.customers-list — browse-all view served by /api/admin/customers.
 *  Returns a flat list with rolled-up stats (conversation_count,
 *  last_activity_at, last_purchase_at, total_spend_aed, order_count,
 *  city) so the page can render the founder-spec'd 6-column table
 *  (ID · Name · City · Lifetime Spend · Last Purchase · Last Contact)
 *  without forcing an explicit phone lookup or a per-row fetch. */
export interface CustomerListItem extends CustomerRecord {
  conversation_count: number;
  last_activity_at: string | null;
  /** §v3.last-contact (2026-05-06) — most recent order alone, distinct
   *  from last_activity_at which considers conversations + tickets too. */
  last_purchase_at?: string | null;
  total_spend_aed: number;
  order_count?: number;
  /** §v3.customer-city (2026-05-06) — last meaningful segment of the
   *  delivery address (non-country). Empty when no order on file. */
  city?: string;
  /** §v3.customer-code (2026-05-06) — friendly tenant-scoped id of
   *  the form ``TT-YYMMDDNN`` (e.g. ``RZ-26050601``). Deterministic
   *  from the customer's earliest-order date + position in that
   *  day's registrations. Empty when the customer has no orders
   *  on file. Used as the displayed Customer ID column and as the
   *  identity reference on orders / support tickets. */
  customer_code?: string;
}

/** §v3.customers-filters (2026-05-06) — list-page filters surfaced
 *  by the customer list card's filter row. All fields optional —
 *  passing nothing returns the unfiltered list.
 *
 *  - ``gender``: ``"male" | "female" | "unknown" | "all"`` (or empty)
 *  - ``city``: case-insensitive substring match
 *  - ``minSpend`` / ``maxSpend``: AED bounds, both inclusive
 */
export interface CustomerListFilters {
  q?: string;
  gender?: "male" | "female" | "unknown" | "all" | "";
  city?: string;
  minSpend?: number | null;
  maxSpend?: number | null;
  /** §v3.customers-date-filters (2026-05-06b) — date-range filter
   *  for ``last_purchase_at``. Both bounds inclusive when set;
   *  format ``YYYY-MM-DD``. */
  lastPurchaseFrom?: string;
  lastPurchaseTo?: string;
  /** §v3.customers-last-contact-filter (2026-05-11, re-added) —
   *  INCLUSIVE range on ``last_activity_at`` (max of order /
   *  conversation / ticket activity). Lets the operator find
   *  e.g. "everyone who reached out in the last week"
   *  independent of whether they bought. */
  lastContactFrom?: string;
  lastContactTo?: string;
  limit?: number;
}

export async function listCustomers(
  qOrFilters: string | CustomerListFilters = "",
  limit: number = 200,
): Promise<{ customers: CustomerListItem[]; count: number }> {
  // Backwards-compat: legacy callers pass a string ``q``.
  const filters: CustomerListFilters =
    typeof qOrFilters === "string"
      ? { q: qOrFilters, limit }
      : qOrFilters;
  const params = new URLSearchParams();
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  if (filters.gender && filters.gender !== "all") {
    params.set("gender", filters.gender);
  }
  if (filters.city?.trim()) params.set("city", filters.city.trim());
  if (filters.minSpend != null && Number.isFinite(filters.minSpend)) {
    params.set("min_spend", String(filters.minSpend));
  }
  if (filters.maxSpend != null && Number.isFinite(filters.maxSpend)) {
    params.set("max_spend", String(filters.maxSpend));
  }
  if (filters.lastPurchaseFrom?.trim()) {
    params.set("last_purchase_from", filters.lastPurchaseFrom.trim());
  }
  if (filters.lastPurchaseTo?.trim()) {
    params.set("last_purchase_to", filters.lastPurchaseTo.trim());
  }
  if (filters.lastContactFrom?.trim()) {
    params.set("last_contact_from", filters.lastContactFrom.trim());
  }
  if (filters.lastContactTo?.trim()) {
    params.set("last_contact_to", filters.lastContactTo.trim());
  }
  const lim = filters.limit ?? 200;
  if (lim) params.set("limit", String(lim));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api<{ customers: CustomerListItem[]; count: number }>(
    `/api/admin/customers${qs}`,
  );
}

/** §v3.customers-filters — manual gender classifier from the
 *  Customer 360 Identity panel. POSTs to a phone-keyed endpoint
 *  rather than a customer-id endpoint because the synthetic
 *  ``cust:<phone>`` ids on the list are derived (not stored), and
 *  ``find_or_create_customer`` is the right side-effect either way.
 *  ``"unknown"`` clears the gender field. */
export async function setCustomerGender(
  phone: string,
  gender: "male" | "female" | "unknown",
): Promise<{ phone: string; customer_id: string; gender: string }> {
  return api<{ phone: string; customer_id: string; gender: string }>(
    `/api/admin/customers/by-phone/${encodeURIComponent(phone)}/gender`,
    { method: "POST", body: { gender } },
  );
}

/** §v3.cp-ident-redesign (2026-05-07) — operator-editable tag list.
 *  Allowlisted server-side to: ``vip | at_risk | loyalty | lapsed``.
 *  Pass an empty array to clear all tags. */
export type CustomerTag = "vip" | "at_risk" | "loyalty" | "lapsed";

export async function setCustomerTags(
  phone: string,
  tags: CustomerTag[],
): Promise<{ phone: string; customer_id: string; tags: string[] }> {
  return api<{ phone: string; customer_id: string; tags: string[] }>(
    `/api/admin/customers/by-phone/${encodeURIComponent(phone)}/tags`,
    { method: "POST", body: { tags } },
  );
}

/** §v3.cp-ident-redesign — operator's free-text note on a customer.
 *  Capped at 2000 chars server-side; empty / whitespace clears it. */
export async function setCustomerNotes(
  phone: string,
  notes: string,
): Promise<{ phone: string; customer_id: string; notes: string }> {
  return api<{ phone: string; customer_id: string; notes: string }>(
    `/api/admin/customers/by-phone/${encodeURIComponent(phone)}/notes`,
    { method: "POST", body: { notes } },
  );
}

/**
 * §E3 / §C2 — Billing / quota state.
 *
 * Shape mirrors ``BillingState.to_json()`` in
 * ``billing_monitor.py:166``. ``level`` progresses:
 *   ok      → funds plentiful, no banner
 *   warning → crossed 75% (configurable) threshold
 *   critical→ crossed 90%
 *   depleted→ 100% spent or upstream 402, NEW SESSIONS BLOCKED
 *
 * Two providers ship today:
 *   * OpenAI — authoritative: polls `/v1/organization/costs` when
 *     an admin key is present, falls back to the local ledger
 *     otherwise. Can reach the `depleted` level.
 *   * Gemini — estimated only. Google has no equivalent admin
 *     cost API, so every figure is a ledger sum. The level caps
 *     at `critical`; `depleted` is reserved for authoritative
 *     "no money left" signals we don't have for Gemini.
 *
 * The dashboard polls ``/api/billing/status`` from
 * ``agent_realtime.py:11722`` on a 60s cadence so Akil sees the
 * warning BEFORE a customer call fails. See
 * ``components/CreditBalanceBanner.tsx``.
 */
export type BillingLevel = "ok" | "warning" | "critical" | "depleted";

export interface BillingProviderState {
  provider: string;
  level: BillingLevel;
  used: number;
  limit: number;
  remaining_fraction: number;
  message: string;
  last_updated_unix: number;
  resets_at_unix: number | null;
  reason_code: string;
  blocks_new_sessions: boolean;
  action_message: string;
}

export interface BillingStatus {
  openai: BillingProviderState;
  /** §C2 — Gemini sibling; always present in the current backend
   *  response. Optional for back-compat with older clients pinned
   *  to the single-provider schema. */
  gemini?: BillingProviderState;
  worst_level: BillingLevel;
  last_check_unix: number;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  return api<BillingStatus>(`/api/billing/status`);
}

/**
 * §C2 voice — report a delta of Gemini Live token usage to the
 * backend ledger. Called from `GeminiLiveSession` every ~30s
 * while the session is live and one final time on session end.
 *
 * The backend endpoint (`POST /api/billing/gemini-live-usage`)
 * persists the delta into the `gemini_usage` SQLite table with
 * `source="voice"`. See `agent_realtime.billing_gemini_live_usage`.
 *
 * The client posts a DELTA (not a cumulative total) so the ledger
 * accumulates naturally. The session class tracks "last flushed"
 * totals and computes the delta; on 5xx/network failure the delta
 * stays pending for the next tick — so one failed flush at 0:30
 * just means 1:00 posts the 0:30+1:00 amount.
 */
export interface GeminiLiveUsageResponse {
  success: boolean;
  recorded_usd_cost: number;
}

export async function recordGeminiLiveUsage(params: {
  input_tokens: number;
  output_tokens: number;
  model?: string;
  /** §D1 follow-up — set true when the session is the Test Voice
   *  playground so the backend tags the ledger row with
   *  ``source="test-voice"`` and the analytics dashboard filters
   *  it out of production spend by default. */
  is_test?: boolean;
}): Promise<GeminiLiveUsageResponse> {
  return api<GeminiLiveUsageResponse>(`/api/billing/gemini-live-usage`, {
    method: "POST",
    body: params,
  });
}

/**
 * §D3 — editable business-details payload.
 *
 * Mirrors the shape of ``/api/admin/business-details`` GET + PUT.
 * ``tenant_id`` + ``provisioned_at`` are read-only and ride along
 * on GET for the UI to key off; the PUT never echoes them back.
 *
 * ``disclosure_language`` accepts ``"en" | "ar" | "both"``.  The
 * backend collapses ``"both"`` to ``"en"`` on the typed
 * ``tenants.disclosure_language`` column but keeps the full value
 * in ``tenant_settings.data_json`` so runtime code that cares
 * about both languages can still read it.
 */
export type DisclosureLanguage = "en" | "ar" | "both";

export interface BusinessDetails {
  tenant_id: string;
  provisioned_at: number | null;
  business_name: string;
  agent_name: string;
  business_description: string;
  phone: string;
  website: string;
  address: string;
  opener_hint: string;
  forward_number: string;
  disclosure_language: DisclosureLanguage | string;
  consent_phone_text_en: string;
  consent_phone_text_ar: string;
  consent_text_chat_en: string;
  consent_text_chat_ar: string;
  operator_email: string;
}

/** Subset a PUT is allowed to set; all optional. */
export type BusinessDetailsPatch = Partial<
  Omit<BusinessDetails, "tenant_id" | "provisioned_at">
>;

export async function getBusinessDetails(): Promise<BusinessDetails> {
  return api<BusinessDetails>(`/api/admin/business-details`);
}

export async function putBusinessDetails(
  patch: BusinessDetailsPatch,
): Promise<BusinessDetails> {
  return api<BusinessDetails>(`/api/admin/business-details`, {
    method: "PUT",
    body: patch,
  });
}

/**
 * §D1 — analytics summary payload.
 *
 * Mirrors the shape of ``GET /api/admin/analytics/summary``.
 * Aggregates across three SQLite sources (unified_history,
 * call_history, usage ledger). The time series is always dense —
 * days with no activity appear as zero rows so chart libraries
 * draw a continuous line without interpolation.
 */
export interface AnalyticsRange {
  from_unix: number;
  to_unix: number;
  days: number;
  tz: string;
}

export interface AnalyticsTotals {
  chats: number;
  calls: number;
  avg_call_duration_seconds: number;
  total_call_duration_seconds: number;
  credits_usd: number;
  credits_openai_usd: number;
  credits_gemini_usd: number;
  unique_customers: number;
}

export interface AnalyticsDailyBucket {
  date: string; // YYYY-MM-DD (UTC)
  chats: number;
  calls: number;
  call_duration_seconds: number;
  openai_usd: number;
  gemini_usd: number;
}

export interface AnalyticsSentimentCounts {
  positive: number;
  neutral: number;
  negative: number;
  unknown: number;
}

export interface AnalyticsSummary {
  range: AnalyticsRange;
  totals: AnalyticsTotals;
  time_series: AnalyticsDailyBucket[];
  channel_breakdown: Record<string, number>;
  sentiment_breakdown: AnalyticsSentimentCounts;
}

export async function getAnalyticsSummary(
  days: number = 30,
  opts: { includeTests?: boolean } = {},
): Promise<AnalyticsSummary> {
  const qs = new URLSearchParams({ days: String(days) });
  if (opts.includeTests) qs.set("include_tests", "1");
  return api<AnalyticsSummary>(`/api/admin/analytics/summary?${qs.toString()}`);
}

// §v3.sentiment-grok-primary (2026-05-11) — kick a Grok-based
// reclassification of chat conversation sentiment. Bounded by
// ``max_rows`` so a single call doesn't burn the LLM budget;
// operator can call again to page through history. ``only_unknown``
// (default true) skips rows that already have a non-empty
// ``sentiment_label`` so re-runs are cheap.
export interface ReclassifySentimentResponse {
  ok: boolean;
  tenant_id: string;
  window_days: number;
  candidate_rows: number;
  processed: number;
  skipped_already_classified: number;
  skipped_no_messages: number;
  counts: {
    positive: number;
    neutral: number;
    negative: number;
    empty: number;
  };
}

export async function reclassifyChatSentiment(opts: {
  days?: number;
  maxRows?: number;
  onlyUnknown?: boolean;
} = {}): Promise<ReclassifySentimentResponse> {
  return api<ReclassifySentimentResponse>(
    "/api/admin/analytics/reclassify-sentiment",
    {
      method: "POST",
      body: {
        days: opts.days ?? 30,
        max_rows: opts.maxRows ?? 200,
        only_unknown: opts.onlyUnknown ?? true,
      },
    },
  );
}

/**
 * §disclosure-toggle — the tenant's AI-disclosure notice (the one-line
 * "you're talking to an AI" said on the first turn). Presence is a
 * per-tenant choice (default on); turning it off requires the owner to
 * acknowledge responsibility for disclosure rules in their markets.
 */
export interface AiDisclosureSettings {
  tenant_id: string;
  enabled: boolean;
  disabled_by?: string | null;
  disabled_at?: number | null;
  text_en: string;
  text_ar: string;
  effective_en_chat: string;
  effective_ar_chat: string;
  effective_en_voice: string;
  effective_ar_voice: string;
  updated_at?: number | null;
  updated_by?: string | null;
}

export async function getAiDisclosure(): Promise<AiDisclosureSettings> {
  return api<AiDisclosureSettings>("/api/settings/ai-disclosure");
}

export async function updateAiDisclosure(patch: {
  enabled?: boolean;
  acknowledge_responsibility?: boolean;
  text_en?: string;
  text_ar?: string;
}): Promise<AiDisclosureSettings> {
  return api<AiDisclosureSettings>("/api/settings/ai-disclosure", {
    method: "PUT",
    body: patch,
  });
}
